import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { PageHeader, SectionCard, EmptyState } from '../ui';
import {
  signupSettingsService,
  type VerifyMode,
  type PendingAccount,
} from '../../services/signupSettingsService';

const MODE_INFO: Record<VerifyMode, { label: string; desc: string }> = {
  auto: {
    label: 'Tự động',
    desc: 'Tài khoản kích hoạt ngay sau khi đăng ký, không gửi email xác minh.',
  },
  manual: {
    label: 'Admin duyệt',
    desc: 'Tài khoản ở trạng thái chờ; quản trị viên duyệt thủ công bên dưới.',
  },
  email: {
    label: 'Xác minh qua email',
    desc: 'Gửi mã 6 số tới email, người dùng tự nhập để kích hoạt. (Cần dịch vụ mail bật)',
  },
};

const fmtDate = (ms: number): string => {
  try {
    return new Date(ms).toLocaleString('vi-VN');
  } catch {
    return String(ms);
  }
};

export const SignupSettings: React.FC = () => {
  const [mode, setMode] = useState<VerifyMode | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingCfg, setLoadingCfg] = useState(true);

  const [pending, setPending] = useState<PendingAccount[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingPending, setLoadingPending] = useState(true);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);

  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const loadConfig = useCallback(async () => {
    setLoadingCfg(true);
    try {
      const cfg = await signupSettingsService.getConfig();
      setMode(cfg.verifyMode);
    } catch (e) {
      setToast({ msg: 'Không tải được cấu hình: ' + (e as Error).message, sev: 'error' });
    } finally {
      setLoadingCfg(false);
    }
  }, []);

  const loadPending = useCallback(async () => {
    setLoadingPending(true);
    try {
      const list = await signupSettingsService.listPending(1, 100);
      setPending(list.accounts);
      setTotal(list.total);
    } catch (e) {
      setToast({ msg: 'Không tải được danh sách chờ: ' + (e as Error).message, sev: 'error' });
    } finally {
      setLoadingPending(false);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
    void loadPending();
  }, [loadConfig, loadPending]);

  const changeMode = async (next: VerifyMode) => {
    if (next === mode) return;
    const prev = mode;
    setMode(next);
    setSaving(true);
    try {
      const res = await signupSettingsService.setMode(next);
      if (!res.result) throw new Error(res.message || 'Đổi chế độ thất bại');
      setToast({ msg: res.message || `Đã đổi chế độ: ${next}`, sev: 'success' });
    } catch (e) {
      setMode(prev); // rollback
      setToast({ msg: (e as Error).message, sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const act = async (email: string, kind: 'approve' | 'reject') => {
    setBusyEmail(email);
    try {
      const res =
        kind === 'approve'
          ? await signupSettingsService.approve(email)
          : await signupSettingsService.reject(email);
      if (!res.result) throw new Error(res.message);
      setToast({ msg: res.message, sev: 'success' });
      setPending((p) => p.filter((a) => a.email !== email));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      setToast({ msg: (e as Error).message, sev: 'error' });
    } finally {
      setBusyEmail(null);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        eyebrow="Đăng ký"
        title="Xác minh tài khoản"
        description="Chọn cách người dùng mới được kích hoạt sau khi đăng ký, và duyệt các tài khoản đang chờ."
      />

      <Stack spacing={3}>
        <SectionCard
          title="Chế độ xác minh"
          description="Áp dụng cho mọi lượt đăng ký mới. Thay đổi có hiệu lực trong khoảng 1 phút."
        >
          {loadingCfg || mode === null ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <RadioGroup
              value={mode}
              onChange={(e) => void changeMode(e.target.value as VerifyMode)}
            >
              <Stack spacing={1.5}>
                {(Object.keys(MODE_INFO) as VerifyMode[]).map((m) => (
                  <FormControlLabel
                    key={m}
                    value={m}
                    disabled={saving}
                    control={<Radio />}
                    sx={{ alignItems: 'flex-start', m: 0 }}
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography sx={{ fontWeight: 600 }}>{MODE_INFO[m].label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {MODE_INFO[m].desc}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </Stack>
            </RadioGroup>
          )}
        </SectionCard>

        <SectionCard
          title={`Tài khoản chờ duyệt${total ? ` (${total})` : ''}`}
          description={
            mode === 'manual'
              ? 'Ở chế độ Admin duyệt, người dùng mới nằm ở đây tới khi được duyệt.'
              : 'Danh sách tài khoản đang ở trạng thái chờ (từ các chế độ trước hoặc mã email chưa nhập).'
          }
          action={
            <Button
              size="small"
              startIcon={<RefreshRoundedIcon />}
              onClick={() => void loadPending()}
              disabled={loadingPending}
            >
              Tải lại
            </Button>
          }
          flush
        >
          {loadingPending ? (
            <Stack alignItems="center" sx={{ py: 5 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : pending.length === 0 ? (
            <Box sx={{ p: 2.5 }}>
              <EmptyState title="Không có tài khoản chờ duyệt" />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Loại</TableCell>
                    <TableCell>Ngày đăng ký</TableCell>
                    <TableCell align="right">Thao tác</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pending.map((a) => (
                    <TableRow key={a.email} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{a.email}</TableCell>
                      <TableCell>{a.userType}</TableCell>
                      <TableCell>{fmtDate(a.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckRoundedIcon />}
                            disabled={busyEmail === a.email}
                            onClick={() => void act(a.email, 'approve')}
                          >
                            Duyệt
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<CloseRoundedIcon />}
                            disabled={busyEmail === a.email}
                            onClick={() => void act(a.email, 'reject')}
                          >
                            Từ chối
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      </Stack>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.sev} onClose={() => setToast(null)} variant="filled">
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};
